import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL as API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const RoleManagement = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = hasPermission('admin', 'view') || user?.is_superuser;
  const [roles, setRoles] = useState([]);
  const [permissionsByScreen, setPermissionsByScreen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'clone'
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permission_ids: []
  });
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load roles and permissions in parallel
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/api/roles'),
        api.get('/api/roles/permissions/all')
      ]);
      
      setRoles(rolesRes.data);
      setPermissionsByScreen(permsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (mode, role = null) => {
    setModalMode(mode);
    setSelectedRole(role);
    
    if (mode === 'edit' && role) {
      // Load full role details with permissions
      try {
        const res = await api.get(`/api/roles/${role.id}`);
        setFormData({
          name: res.data.name,
          description: res.data.description || '',
          permission_ids: res.data.permissions.map(p => p.id)
        });
      } catch (err) {
        console.error('Error loading role:', err);
        alert('Failed to load role details');
        return;
      }
    } else if (mode === 'clone' && role) {
      // Load full role details and prepare clone
      try {
        const res = await api.get(`/api/roles/${role.id}`);
        setFormData({
          name: `${res.data.name} (Copy)`,
          description: res.data.description || '',
          permission_ids: res.data.permissions.map(p => p.id)
        });
      } catch (err) {
        console.error('Error loading role:', err);
        alert('Failed to load role details');
        return;
      }
    } else {
      // Reset form for new role
      setFormData({
        name: '',
        description: '',
        permission_ids: []
      });
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRole(null);
    setFormData({
      name: '',
      description: '',
      permission_ids: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      alert('Role name is required');
      return;
    }
    
    if (formData.permission_ids.length === 0) {
      if (!window.confirm('This role has no permissions. Continue?')) {
        return;
      }
    }
    
    try {
      if (modalMode === 'create' || modalMode === 'clone') {
        // Create new role
        await api.post('/api/roles', {
          name: formData.name,
          description: formData.description,
          permission_ids: formData.permission_ids
        });
        alert('Role created successfully!');
      } else {
        // Update existing role
        await api.put(`/api/roles/${selectedRole.id}`, {
          name: formData.name,
          description: formData.description
        });
        
        // Update permissions separately
        await api.post(`/api/roles/${selectedRole.id}/permissions`, {
          permission_ids: formData.permission_ids
        });
        
        alert('Role updated successfully!');
      }
      
      handleCloseModal();
      loadData();
    } catch (err) {
      console.error('Error saving role:', err);
      alert(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    
    try {
      await api.delete(`/api/roles/${roleToDelete.id}`);
      alert('Role deleted successfully!');
      setShowDeleteConfirm(false);
      setRoleToDelete(null);
      loadData();
    } catch (err) {
      console.error('Error deleting role:', err);
      alert(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleTogglePermission = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }));
  };

  const handleSelectAllInScreen = (screen) => {
    const screenPermIds = screen.permissions.map(p => p.id);
    const allSelected = screenPermIds.every(id => formData.permission_ids.includes(id));
    
    if (allSelected) {
      // Deselect all in this screen
      setFormData(prev => ({
        ...prev,
        permission_ids: prev.permission_ids.filter(id => !screenPermIds.includes(id))
      }));
    } else {
      // Select all in this screen
      setFormData(prev => ({
        ...prev,
        permission_ids: [...new Set([...prev.permission_ids, ...screenPermIds])]
      }));
    }
  };

  const handleSelectAll = () => {
    const allPermIds = permissionsByScreen.flatMap(s => s.permissions.map(p => p.id));
    const allSelected = allPermIds.every(id => formData.permission_ids.includes(id));
    
    if (allSelected) {
      setFormData(prev => ({ ...prev, permission_ids: [] }));
    } else {
      setFormData(prev => ({ ...prev, permission_ids: allPermIds }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading roles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-red-800 font-semibold">Error</div>
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Role Management</h1>
          <p className="text-gray-600 mt-1">Manage roles and their permissions</p>
        </div>
        <button
          onClick={() => handleOpenModal('create')}
          className="bg-[#C7AE6E] hover:bg-[#B49A5F] text-white px-6 py-2 rounded-lg font-semibold transition-colors"
        >
          + Add New Role
        </button>
      </div>

      {/* Roles Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Users
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{role.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600">{role.description || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {role.permission_count} permissions
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {role.user_count} users
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {role.is_system ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      System
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      Custom
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleOpenModal('edit', role)}
                    className="text-[#C7AE6E] hover:text-[#9F8752] mr-3 font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleOpenModal('clone', role)}
                    className="text-[#C7AE6E] hover:text-[#9F8752] mr-3 font-medium"
                  >
                    Clone
                  </button>
                  {!role.is_system && (
                    <button
                      onClick={() => {
                        setRoleToDelete(role);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {roles.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No roles found. Click "Add New Role" to create one.
          </div>
        )}
      </div>

      {/* Create/Edit Role Side Panel */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={handleCloseModal}
          />
          
          {/* Side Panel */}
<div className="fixed inset-y-0 right-0 w-[1100px] bg-white shadow-2xl z-50 overflow-hidden transform transition-transform duration-300 ease-out border-l-4 border-[#C7AE6E] animate-slide-in">
            <div className="h-full flex flex-col">
              {/* Panel Header - Fixed */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {modalMode === 'create' ? '➕ Create New Role' : 
                     modalMode === 'clone' ? '📋 Clone Role' : '✏️ Edit Role'}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {modalMode === 'create' && 'Create a custom role with specific permissions'}
                    {modalMode === 'clone' && 'Create a copy of this role with the same permissions'}
                    {modalMode === 'edit' && 'Modify role details and permissions'}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full p-2 transition-colors"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Panel Body - Role Info (Fixed) */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <form onSubmit={handleSubmit}>
                  {/* Role Name and Description - One Line */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Role Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C7AE6E] bg-white"
                        required
                        disabled={modalMode === 'edit' && selectedRole?.is_system}
                      />
                      {modalMode === 'edit' && selectedRole?.is_system && (
                        <p className="text-xs text-gray-500 mt-1">System roles cannot be renamed</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C7AE6E] bg-white"
                        placeholder="Brief description of this role..."
                      />
                    </div>
                  </div>
                </form>
              </div>
              
              {/* Panel Body - Permissions (Scrollable) */}
              <div className="flex-1 overflow-y-auto p-6 bg-white">

                {/* Permissions Section */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 text-lg">
                        Permissions Matrix
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {formData.permission_ids.length} permission(s) selected
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="text-sm text-[#C7AE6E] hover:text-[#9F8752] font-medium"
                    >
                      {formData.permission_ids.length === permissionsByScreen.flatMap(s => s.permissions).length
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  </div>

                  {/* Permission Matrix - Table Format */}
                  <div className="border border-gray-300 rounded-lg overflow-auto max-h-[calc(100vh-420px)] bg-white">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300 bg-gray-50">
                            Screen / Section
                          </th>
                          {/* Get unique actions across all screens */}
                          {['view', 'create', 'edit', 'delete', 'approve', 'export', 'configure'].map((action) => {
                            const hasAction = permissionsByScreen.some(s => 
                              s.permissions.some(p => p.action === action)
                            );
                            return hasAction ? (
                              <th key={action} className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                                {action}
                              </th>
                            ) : null;
                          })}
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                            All
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {permissionsByScreen
                          .filter(screen => isAdmin || !screen.screen_key.startsWith('admin'))
                          .map((screen) => {
                          const screenPermIds = screen.permissions.map(p => p.id);
                          const allScreenSelected = screenPermIds.every(id => formData.permission_ids.includes(id));
                          
                          return (
                            <tr key={screen.screen_key} className="hover:bg-[#FFF8E7] transition-colors">
                              {/* Screen Name */}
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-300 bg-gray-50">
                                {screen.screen_label}
                                <span className="ml-2 text-xs text-gray-500">
                                  ({screenPermIds.filter(id => formData.permission_ids.includes(id)).length}/{screen.permissions.length})
                                </span>
                              </td>
                              
                              {/* Permission Checkboxes */}
                              {['view', 'create', 'edit', 'delete', 'approve', 'export', 'configure'].map((action) => {
                                const hasAction = permissionsByScreen.some(s => 
                                  s.permissions.some(p => p.action === action)
                                );
                                if (!hasAction) return null;
                                
                                const perm = screen.permissions.find(p => p.action === action);
                                
                                return (
                                  <td key={action} className="px-3 py-3 text-center border-r border-gray-200">
                                    {perm ? (
                                      <input
                                        type="checkbox"
                                        checked={formData.permission_ids.includes(perm.id)}
                                        onChange={() => handleTogglePermission(perm.id)}
                                        className="w-4 h-4 border-gray-300 rounded focus:ring-gray-500 cursor-pointer accent-gray-700"
                                        style={{ accentColor: '#374151' }}
                                      />
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                );
                              })}
                              
                              {/* Select All for Row */}
                              <td className="px-3 py-3 text-center bg-gray-50">
                                <input
                                  type="checkbox"
                                  checked={allScreenSelected}
                                  onChange={() => handleSelectAllInScreen(screen)}
                                  className="w-4 h-4 border-gray-300 rounded focus:ring-gray-500 cursor-pointer accent-gray-700"
                                  style={{ accentColor: '#374151' }}
                                  title="Select/Deselect all for this screen"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Panel Footer - Fixed */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="px-6 py-2 bg-[#C7AE6E] hover:bg-[#B49A5F] text-white rounded-lg font-semibold transition-colors"
                  >
                    {modalMode === 'create' || modalMode === 'clone' ? '✓ Create Role' : '✓ Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && roleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the role <strong>{roleToDelete.name}</strong>?
              {roleToDelete.user_count > 0 && (
                <span className="block mt-2 text-red-600 font-semibold">
                  Warning: {roleToDelete.user_count} user(s) have this role!
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setRoleToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
              >
                Delete Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;

